import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Play, ArrowRight } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { videoTestimonials, VideoTestimonial } from '@/data/videoTestimonialsData';
import { SimpleReveal } from './SimpleReveal';
import { trackLead } from '@/lib/metaPixel';

const APPLY_URL = 'https://a23j5fry30t.typeform.com/to/CzjaFT5z';

interface VideoCardProps {
  testimonial: VideoTestimonial;
  onPlay: (testimonial: VideoTestimonial) => void;
}

const VideoCard = ({ testimonial, onPlay }: VideoCardProps) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(
    testimonial.thumbnailUrl || null
  );
  const [isHovering, setIsHovering] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Extract video ID for embed
  const videoId = testimonial.videoUrl?.match(/vimeo\.com\/(\d+)/)?.[1];

  // Auto-fetch Vimeo thumbnail
  useEffect(() => {
    if (!thumbnailUrl && testimonial.videoUrl && videoId) {
      fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`)
        .then(res => res.json())
        .then(data => {
          const largeThumbnail = data.thumbnail_url?.replace(/_\d+x\d+/, '_640x360');
          setThumbnailUrl(largeThumbnail || data.thumbnail_url);
        })
        .catch(() => {/* Fallback to gradient placeholder */});
    }
  }, [testimonial.videoUrl, thumbnailUrl, videoId]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  const handleMouseEnter = () => {
    setIsHovering(true);
    hoverTimerRef.current = setTimeout(() => {
      setShowPreview(true);
    }, 100);
  };

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setIsHovering(false);
    setShowPreview(false);
  };

  return (
    <div 
      className="group cursor-pointer"
      onClick={() => onPlay(testimonial)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Video Thumbnail with Gradient Border + Blue Glow on Hover */}
      <div className="gradient-border-card relative aspect-video rounded-xl overflow-hidden bg-white/5 mb-4 transition-all duration-[400ms] ease-out group-hover:shadow-[0_0_40px_rgba(0,113,227,0.25)]">
        {/* Metric Chip */}
        <div className="absolute top-3 left-3 px-2.5 py-1 bg-[#0071e3] text-white text-xs font-semibold rounded-full shadow-lg z-10">
          {testimonial.metric}
        </div>

        {/* Thumbnail - always visible as base layer */}
        {thumbnailUrl ? (
          <img 
            src={thumbnailUrl} 
            alt={`${testimonial.artistName} testimonial`}
            className="w-full h-full object-cover transition-all duration-[400ms] ease-out group-hover:scale-[1.02] group-hover:brightness-110"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
            <span className="text-white/30 text-sm font-medium">{testimonial.artistName}</span>
          </div>
        )}

        {/* Video preview - layered on top when hovering with audio */}
        {showPreview && videoId && (
          <>
            <iframe
              key={`preview-${videoId}-${isHovering}`}
              src={`https://player.vimeo.com/video/${videoId}?autoplay=1&muted=0&loop=1&background=1`}
              className="absolute inset-0 w-full h-full z-[1] pointer-events-none"
              allow="autoplay"
              title={`${testimonial.artistName} preview`}
            />
            {/* Invisible clickable overlay - allows clicks to pass through to parent */}
            <div className="absolute inset-0 z-[2]" />
          </>
        )}
        
        {/* Play Button Overlay - blurred glass style, hide when preview is playing */}
        {!showPreview && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors duration-[400ms] group-hover:bg-black/35">
            <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg transition-all duration-[400ms] ease-out group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]">
              <Play className="w-6 h-6 lg:w-7 lg:h-7 text-white ml-1" fill="currentColor" />
            </div>
          </div>
        )}
      </div>

      {/* Quote and Name */}
      <div className="px-1">
        <p className="text-white/80 text-sm lg:text-base leading-relaxed mb-2">
          {testimonial.quote}
        </p>
        <p className="text-white font-semibold text-sm lg:text-base">{testimonial.artistName}</p>
      </div>
    </div>
  );
};

export const VideoTestimonialsCarousel = () => {
  const [selectedVideo, setSelectedVideo] = useState<VideoTestimonial | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [api, setApi] = useState<any>(null);
  const [current, setCurrent] = useState(0);

  // Track current slide
  useEffect(() => {
    if (!api) return;
    
    setCurrent(api.selectedScrollSnap());
    
    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const handlePlay = useCallback((testimonial: VideoTestimonial) => {
    if (!testimonial.videoUrl) {
      console.log('Video URL not yet available for:', testimonial.artistName);
      return;
    }
    setSelectedVideo(testimonial);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => setSelectedVideo(null), 200);
  }, []);

  const getVimeoEmbedUrl = (url: string) => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    if (match) {
      return `https://player.vimeo.com/video/${match[1]}?autoplay=1&title=0&byline=0&portrait=0`;
    }
    return url;
  };

  const scrollTo = useCallback((index: number) => {
    api?.scrollTo(index);
  }, [api]);

  return (
    <section className="pb-16 lg:pb-24 relative">
      {/* Ambient blue glow behind carousel */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 70%, rgba(0,113,227,0.12), transparent 70%)'
        }}
      />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <SimpleReveal>
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#0071e3]/15 hover:border-[#0071e3]/35 hover:shadow-[0_0_20px_rgba(0,113,227,0.3)]">
              <span className="font-mono text-xs font-medium text-[#0071e3] tracking-wider md:tracking-[0.15em] uppercase">
                &gt; ARTIST STORIES
              </span>
            </div>
          </div>
        </SimpleReveal>

        <SimpleReveal className="text-center">
          <h2 className="headline-lg text-white w-fit mx-auto mb-3">
            Here's What{' '}
            <span className="inline-block bg-[#f5f5f7] text-[#1d1d1f] px-3 py-1 rounded-lg font-bold -rotate-2 shadow-[0_0_40px_rgba(0,113,227,0.7)]">
              Happened
            </span>
          </h2>
        </SimpleReveal>

        <SimpleReveal>
          <p className="body-large text-white/70 text-center max-w-2xl mx-auto mb-12">
            Here's how we helped real artists next to the next stage in their career
          </p>
        </SimpleReveal>

        {/* Carousel with staggered reveal */}
        <SimpleReveal delay={200}>
          <Carousel
            opts={{
              align: 'start',
              loop: true,
            }}
            className="w-full"
            setApi={setApi}
          >
            <CarouselContent className="-ml-4 lg:-ml-6">
              {videoTestimonials.map((testimonial, index) => (
                <CarouselItem 
                  key={testimonial.id} 
                  className="pl-4 lg:pl-6 basis-full sm:basis-1/2 lg:basis-1/3"
                >
                  <SimpleReveal delay={index * 150}>
                    <VideoCard testimonial={testimonial} onPlay={handlePlay} />
                  </SimpleReveal>
                </CarouselItem>
              ))}
            </CarouselContent>
            
            {/* Navigation Arrows - Desktop only */}
            <CarouselPrevious className="hidden lg:flex -left-4 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white" />
            <CarouselNext className="hidden lg:flex -right-4 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white" />
          </Carousel>
        </SimpleReveal>

        {/* Clickable Progress Dots - Mobile only */}
        <div className="flex justify-center gap-3 mt-8 lg:hidden">
          {videoTestimonials.slice(0, 4).map((_, index) => (
            <button 
              key={index}
              onClick={() => scrollTo(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                current === index 
                  ? 'bg-[#0071e3] scale-110' 
                  : 'bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Video Modal - Same for mobile and desktop */}
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="w-[95vw] sm:max-w-5xl p-0 bg-[#0a0a0a] border-white/10 overflow-hidden [&>button]:text-white [&>button]:hover:bg-white/10">
          {selectedVideo?.videoUrl && (
            <>
              <div className="aspect-video w-full">
                <iframe
                  src={getVimeoEmbedUrl(selectedVideo.videoUrl)}
                  className="w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={`${selectedVideo.artistName} testimonial video`}
                />
              </div>
              
              {/* CTA Section */}
              <div className="p-4 sm:p-6 text-center border-t border-white/10">
                <p className="text-white/70 text-xs sm:text-sm mb-3 sm:mb-4">
                  Ready to get results like this?
                </p>
                <Button variant="hero-blue-ring" size="hero" asChild className="group text-sm sm:text-base cta-pulse">
                  <a href={APPLY_URL} target="_blank" rel="noopener noreferrer" onClick={trackLead}>
                    Start Your Application
                    <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default VideoTestimonialsCarousel;
